import type { VoidFunctionComponent } from 'react';
import { useEffect, useState } from 'react';
import type Stripe from 'stripe-latest';
import usePlans from './usePlans';
import Loader from '../../../generic/Loader';
import Card from '../../../generic/Card/Card';
import Flex from '../../../generic/Flex';
import Button from '../../../generic/Button';
import RangeSlider from '../../../generic/RangeSlider';
import type { Step } from '../../../generic/RangeSlider';
import LimitsComponent from '../Limits';
import FeaturesComponent from '../Features';
import type { Plan } from '../../../../../shared/subscriptions';
import { calculateDiscountPercentage, formatPrice, isEnterprisePlan, isFreePlan, planIcon } from '../utils';
import styles from './styles.module.less';

export type ViewIntervalOptions = 'month' | 'year';

const Plans = () => {
  const [viewInterval, setViewInterval] = useState<ViewIntervalOptions>('year');

  const {
    data,
    isLoading,
  } = usePlans();

  if (isLoading) {
    return (<Loader />);
  }

  if (!data) {
    return null;
  }

  return (
    <>
      <Flex
        direction="row"
        align="center"
        gap={20}
        className={styles.plansController}
      >
        <span className={styles.plansControllerTitle}>
          Change Plan
        </span>
        <span>
          Billing
        </span>
        <div className={styles.toggleButtonsContainer}>
          <Button
            onClick={() => setViewInterval('year')}
            type={viewInterval === 'year' ? 'primary' : 'outline'}
            size="large"
          >
            Yearly
          </Button>
          <Button
            onClick={() => setViewInterval('month')}
            type={viewInterval === 'month' ? 'primary' : 'outline'}
            size="large"
          >
            Monthly
          </Button>
        </div>
      </Flex>

      <Flex direction="row" align="unset" gap={32}>
        {data.map((plan) => {
          return (
            <Flex direction="row" align="unset" key={plan.product.name}>
              <Card
                isFullWidth
                isContentFullHeight
                variant="fill"
                key={plan.product.name}
              >
                <Card.Content>
                  <div className={styles.cardContentContainer}>
                    <Flex direction="column" align="flex-start" justify="flex-start" gap={20}>
                      <img src={planIcon(plan.product)} alt={plan.product.name} />

                      <div className={styles.planName}>
                        {plan.product.name}
                      </div>

                      <div className={styles.planDescription}>
                        {plan.product.description}
                      </div>

                      {plan.prices.length === 1 ?
                        <DisplaySinglePrice price={plan.prices[0] as Stripe.Price} />
                        :
                        <DisplayPrices plan={plan} viewInterval={viewInterval} />}
                    </Flex>
                  </div>
                </Card.Content>
              </Card>
            </Flex>
          );
        })}
      </Flex>
    </>
  );
};

type DisplayPricesProps = {
  plan: Plan,
  viewInterval: ViewIntervalOptions,
};

/**
 * Renders a range slider for selecting a price tier and displays associated limits and features.
 * Displays a list of available prices, allowing users to choose a price level and view relevant
 * features and usage limits for the selected tier.
 * Typically used for plans that have multiple price options, such as BUSINESS.
 */
const DisplayPrices: VoidFunctionComponent<DisplayPricesProps> = ({
  plan,
  viewInterval,
}) => {
  const [filteredPrices, setFilteredPrices] = useState<Stripe.Price[]>([]);
  const [selectedPrice, setSelectedPrice] = useState<Stripe.Price | null>(null);
  const handlePriceChange = (priceId: string) => {
    const price = plan.prices.find((p) => p.id === priceId) as Stripe.Price || null;
    setSelectedPrice(price);
  };
  const generateSteps = () => {
    return filteredPrices
      .map((price) => {
        return {
          label: price.nickname,
          value: price.id,
        } as Step;
      });
  };

  /**
   * Calculate the discount percentage between the yearly & monthly rate for the selected price
   */
  const getDiscount = () => {
    if (!selectedPrice) {
      return null;
    }

    if (selectedPrice.recurring?.interval === 'month') {
      return null;
    }

    const monthlyPrice = plan.prices.find((p) => p.nickname === selectedPrice.nickname);
    if (!monthlyPrice) {
      return null;
    }

    if (!monthlyPrice.unit_amount || !selectedPrice.unit_amount) {
      return null;
    }

    return calculateDiscountPercentage((monthlyPrice.unit_amount * 12), selectedPrice.unit_amount);
  };

  useEffect(() => {
    const prices = plan.prices.filter(price => price?.recurring?.interval === viewInterval);
    setFilteredPrices(prices);
    setSelectedPrice(prices[0] as Stripe.Price);
  }, [plan.prices, viewInterval]);

  if (!selectedPrice) {
    return null;
  }

  return (
    <>
      <DisplayDiscount value={getDiscount()} />
      <span className={styles.price}>{formatPrice(selectedPrice.unit_amount || 0, viewInterval)}</span>
      <div className={styles.priceSliderContainer}>
        <RangeSlider
          steps={generateSteps()}
          onChange={handlePriceChange}
          defaultStepValue={selectedPrice.id}
        />
      </div>
      <LimitsComponent price={selectedPrice} />
      <FeaturesComponent product={selectedPrice.product as Stripe.Product} />
    </>
  );
};

type DisplayDiscountProps = {
  value: number | null,
};
const DisplayDiscount: VoidFunctionComponent<DisplayDiscountProps> = ({
  value,
}) => {
  if (!value) {
    return null;
  }
  return (
    <div className={styles.discount}>
      {value}% discount
    </div>
  );
};

type DisplaySinglePriceProps = {
  price: Stripe.Price,
};

/**
 * Renders a single price component for plans with a fixed price tier.
 * Typically used for plans that have only one price option, such as FREE and ENTERPRISE.
 */
const DisplaySinglePrice: VoidFunctionComponent<DisplaySinglePriceProps> = ({
  price,
}) => {
  const product = price.product as Stripe.Product;
  const isFree = isFreePlan(product);
  const isEnterprise = isEnterprisePlan(product);

  return (
    <>
      {isFree && <DisplayFreePrice />}
      {isEnterprise && <DisplayEnterprisePrice />}
      <LimitsComponent price={price} />
      <FeaturesComponent product={product} />
    </>
  );
};

/**
 * Renders the price component specifically for the FREE plan.
 * Displays information related to the free pricing tier.
 */
const DisplayFreePrice = () => {
  return (
    <>
      <span className={styles.price}>$0/month</span>
      <Card
        isFullWidth
        padding="xs"
        variant="outline"
      >
        <Card.Content>
          <span className={styles.priceTierDescription}>One size fits all</span>
        </Card.Content>
      </Card>
    </>
  );
};

/**
 * Renders the price component specifically for the ENTERPRISE plan.
 * Displays pricing details and features related to the enterprise pricing tier.
 */
const DisplayEnterprisePrice = () => {
  return (
    <>
      <span className={styles.price}>Custom Pricing</span>
      <Card
        isFullWidth
        padding="xs"
        variant="outline"
      >
        <Card.Content>
          <span className={styles.priceTierDescription}>Unlimited scaling for enterprises</span>
        </Card.Content>
      </Card>
    </>
  );
};

export default Plans;
